import React, { useCallback, useEffect, useState } from 'react';
import { whatsappAPI } from './whatsappAPI';

/* Setores, etiquetas e situações do atendimento — compartilhado entre a tela de
 * Atendimento e a de Configurações. Cores vêm do cadastro (hex) e são usadas com
 * transparência para funcionar no tema claro e no escuro. */

export const PALETA = ['#059669', '#0d9488', '#0284c7', '#2563eb', '#7c3aed', '#a855f7',
  '#db2777', '#e11d48', '#dc2626', '#ea580c', '#ca8a04', '#65a30d', '#64748b'];

export const SITUACOES = [
  { id: 'em_atendimento', label: 'Em atendimento', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', dot: 'bg-emerald-500' },
  { id: 'proposta_enviada', label: 'Proposta enviada', cls: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200', dot: 'bg-indigo-500' },
  { id: 'aguardando_cliente', label: 'Aguardando cliente', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200', dot: 'bg-sky-500' },
  { id: 'aguardando_setor', label: 'Aguardando setor', cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200', dot: 'bg-orange-500' },
  { id: 'aguardando_pagamento', label: 'Aguardando pagamento', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200', dot: 'bg-yellow-500' },
  { id: 'resolvido', label: 'Resolvido', cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200', dot: 'bg-teal-500' },
];
export const situacaoInfo = (id) => SITUACOES.find(s => s.id === id);

// cache simples entre telas: setores/etiquetas mudam pouco
let cache = null;
const ouvintes = new Set();
async function buscar() {
  const [s, e] = await Promise.all([
    whatsappAPI.setores().then(r => r.data || []).catch(() => []),
    whatsappAPI.etiquetas().then(r => r.data || []).catch(() => []),
  ]);
  cache = { setores: s, etiquetas: e };
  ouvintes.forEach(fn => fn(cache));
  return cache;
}
export const recarregarOrganizacao = () => buscar();

export function useOrganizacao() {
  const [dados, setDados] = useState(cache || { setores: [], etiquetas: [] });
  useEffect(() => {
    ouvintes.add(setDados);
    if (!cache) buscar(); else setDados(cache);
    return () => { ouvintes.delete(setDados); };
  }, []);
  const recarregar = useCallback(() => buscar(), []);
  return { ...dados, recarregar };
}

/* ---------- configuração do atendimento (cache entre telas; só leitura) ---------- */
let cfgCache = null;
let cfgPromessa = null;
const cfgOuvintes = new Set();
export function recarregarCfgAtendimento() {
  cfgPromessa = whatsappAPI.config().then(r => { cfgCache = r.data || {}; cfgOuvintes.forEach(fn => fn(cfgCache)); return cfgCache; })
    .catch(() => cfgCache || {}).finally(() => { cfgPromessa = null; });
  return cfgPromessa;
}
export function useCfgAtendimento() {
  const [cfg, setCfg] = useState(cfgCache);
  useEffect(() => {
    cfgOuvintes.add(setCfg);
    if (!cfgCache && !cfgPromessa) recarregarCfgAtendimento(); else if (cfgCache) setCfg(cfgCache);
    return () => { cfgOuvintes.delete(setCfg); };
  }, []);
  return cfg;
}


/* Telefone visível. Contatos que o WhatsApp mostra só pelo LID (número oculto) não têm telefone:
   o LID tem 14-15 dígitos e não começa com 55 — formatá-lo dava "+23 74 6464818-3875". */
export function ehLid(c) {
  const n = String(c?.numero ?? '');
  return !!(c?.jid?.endsWith('@lid') || c?.lid || (n.length >= 14 && !n.startsWith('55')));
}
export function fmtTelefone(n = '') {
  const d = String(n).replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) return `+55 (${d.slice(2, 4)}) ${d.slice(4, -4)}-${d.slice(-4)}`;
  return `+${d}`;
}
// número informado (pelo contato, ao assistente, ou por um atendente) para quem o WhatsApp esconde o número
export const telefoneConversa = (c) => (c?.numero_informado && c?.numero ? fmtTelefone(c.numero)
  : ehLid(c) ? '' : fmtTelefone(c?.numero));
export const numeroInformado = (c) => !!(c?.numero_informado && c?.numero);

export function Chip({ cor = '#64748b', children, small, title, onRemove }) {
  return (
    <span title={title}
      className={`inline-flex items-center gap-1 rounded-full font-medium shrink-0 max-w-[140px] ${small ? 'text-[10.5px] px-1.5 py-px' : 'text-[11px] px-2 py-0.5'}`}
      style={{ background: `${cor}1f`, color: cor, boxShadow: `inset 0 0 0 1px ${cor}40` }}>
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cor }} />
      <span className="truncate">{children}</span>
      {onRemove && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="ml-0.5 opacity-60 hover:opacity-100" aria-label="Remover">×</button>
      )}
    </span>
  );
}

export function SeletorCor({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {PALETA.map(c => (
        <button key={c} type="button" onClick={() => onChange(c)} aria-label={`Cor ${c}`}
          className={`w-6 h-6 rounded-full transition-transform ${value === c ? 'ring-2 ring-offset-2 ring-slate-400 dark:ring-offset-slate-900 scale-110' : 'hover:scale-110'}`}
          style={{ background: c }} />
      ))}
    </div>
  );
}
